import { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import LendyIcon from "../components/lendy/LendyIcon";
import "./rowMenu.css";

// The web app's Scenario Center task menu (ScenarioTaskMenu): 220px, 8px corners, a strong border.
const MENU_PAPER_SX = {
  width: 220,
  mt: 0.5,
  py: 0.5,
  borderRadius: "8px",
  border: "1px solid var(--border-strong)",
  backgroundColor: "var(--surface-raised)",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
  backgroundImage: "none",
};

const MENU_ITEM_SX = {
  minHeight: 0,
  gap: 1,
  px: 1.5,
  py: 1.25,
  // The menu renders outside the sidebar's root, so it names the sidebar's font itself.
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--text-primary)",
  "&:hover, &.Mui-focusVisible": { backgroundColor: "var(--selected-color)" },
};

/**
 * A row's kebab and its menu, like a Scenario Center task card's. An item with `items` opens them in
 * the same menu, with a way back, so a narrow sidebar never needs a flyout.
 */
const RowMenu = ({ label, items, onOpenChange }) => {
  const [anchor, setAnchor] = useState(null);
  const [submenu, setSubmenu] = useState(null);

  const open = (event) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
    onOpenChange?.(true);
  };

  const close = () => {
    setAnchor(null);
    setSubmenu(null);
    onOpenChange?.(false);
  };

  const pick = (item) => {
    if (item.items) {
      setSubmenu(item);

      return;
    }

    close();
    item.onSelect();
  };

  const shown = submenu ? submenu.items : items;

  return (
    <>
      <button
        type="button"
        className="aav2-row-menu-trigger"
        aria-label={`More actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        data-open={anchor ? "" : undefined}
        onClick={open}
      >
        <LendyIcon name="moreVertical" size={14} />
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: MENU_PAPER_SX }}
        MenuListProps={{ "aria-label": `Actions for ${label}`, sx: { p: 0 } }}
        transitionDuration={100}
      >
        {submenu && (
          <MenuItem sx={{ ...MENU_ITEM_SX, color: "var(--text-secondary)" }} onClick={() => setSubmenu(null)}>
            <span className="aav2-row-menu-icon" aria-hidden="true">
              <LendyIcon name="left" size={16} />
            </span>
            {submenu.label}
          </MenuItem>
        )}
        {shown.map((item) => (
          <MenuItem
            key={item.id}
            sx={item.isDanger ? { ...MENU_ITEM_SX, color: "var(--danger-fg)" } : MENU_ITEM_SX}
            onClick={() => pick(item)}
          >
            <span className={`aav2-row-menu-icon${item.isDanger ? " is-danger" : ""}`} aria-hidden="true">
              <LendyIcon name={item.icon} size={16} />
            </span>
            <span className="aav2-row-menu-label">{item.label}</span>
            {item.items && (
              <span className="aav2-row-menu-icon" aria-hidden="true">
                <LendyIcon name="right" size={14} />
              </span>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RowMenu;
